import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { BanknotesIcon, ArrowPathIcon, CalculatorIcon, UsersIcon, CheckIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { chargeCalculationService, type ChargeCalculation, type ChargeCalculationItem } from '../services/chargeCalculationService';
import { companySettingsService } from '@/services/companySettingsService';
import { listPayrollImports } from '@/features/Expenses/services/payrollImportsService'; // Importing directly from file instead of index if index is missing it
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';
import CurrencyInput from '@/components/shared/CurrencyInput/CurrencyInput';

// Helper
const formatCurrency = (val: number) => val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default function ChargeCalculationDetailsPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [calculation, setCalculation] = useState<ChargeCalculation | null>(null);
    const [loading, setLoading] = useState(true);
    const [isCalculating, setIsCalculating] = useState(false);

    // Payroll Link State
    const [availableImports, setAvailableImports] = useState<any[]>([]);
    const [selectedImportId, setSelectedImportId] = useState('');

    const loadData = async () => {
        setLoading(true);
        try {
            if (id === 'new') {
                const state = location.state as { competence: string; monthlyRevenue: number; payrollImportId: string } | undefined;
                if (!state) {
                    toast.error('Dados iniciais não encontrados');
                    navigate('/expenses/charges');
                    return;
                }

                // Load payroll imports
                const imports = await listPayrollImports();
                setAvailableImports(imports.filter(i => i.competence === state.competence));

                // Load Settings for the chosen competence
                const date = `${state.competence}-01`;
                const settings = await companySettingsService.getSettingsForDate(date);

                // Auto-calculate if payroll ID is present
                let calculatedState: Partial<ChargeCalculation> = {
                    totalBase: 0,
                    totalCharges: 0,
                    totalInssCompany: 0,
                    totalRat: 0,
                    totalThirdParties: 0,
                    totalCprb: 0,
                    items: [],
                };

                if (state.payrollImportId && settings) {
                    try {
                        const { getPayrollImportById } = await import('@/features/Expenses/services');
                        const payrollData = await getPayrollImportById(state.payrollImportId);

                        if (payrollData) {
                            const appliedInssRate = (settings.inssEmployerRate || 0) / 100;
                            const appliedCprbRate = (settings.cprbRate || 0) / 100;
                            const appliedRatRate = ((settings.rat || 0) / 100) * (settings.fap || 1);
                            const appliedThirdsRate = (settings.thirdParties || 0) / 100;

                            const newItems = payrollData.collaborators.map(col => {
                                const base = col.earningsTotal || 0;
                                let inss = 0, rat = 0, thirds = 0;

                                if (settings.regime !== 'SIMPLES') {
                                    inss = base * appliedInssRate;
                                    rat = base * appliedRatRate;
                                    thirds = base * appliedThirdsRate;
                                }

                                return {
                                    collaboratorId: col.collaboratorId,
                                    collaboratorName: col.collaboratorName,
                                    baseValue: base,
                                    inssCompany: inss,
                                    rat: rat,
                                    thirdParties: thirds,
                                    total: inss + rat + thirds
                                };
                            });

                            const totalBase = newItems.reduce((sum, item) => sum + item.baseValue, 0);
                            const totalInss = newItems.reduce((sum, item) => sum + item.inssCompany, 0);
                            const totalRat = newItems.reduce((sum, item) => sum + item.rat, 0);
                            const totalThirds = newItems.reduce((sum, item) => sum + item.thirdParties, 0);

                            const revenue = state.monthlyRevenue || 0;
                            const totalCprb = revenue * appliedCprbRate;
                            const totalCharges = totalInss + totalRat + totalThirds + totalCprb;

                            calculatedState = {
                                totalBase,
                                totalCharges,
                                totalInssCompany: totalInss,
                                totalRat,
                                totalThirdParties: totalThirds,
                                totalCprb,
                                items: newItems,
                            };
                            toast.success('Cálculo inicial realizado automaticamente.');
                        }
                    } catch (err) {
                        console.error('Erro no cálculo automático inicial', err);
                    }
                }

                setCalculation({
                    id: 'new', // Temporary ID
                    competence: state.competence,
                    monthlyRevenue: state.monthlyRevenue,
                    payrollImportId: state.payrollImportId,
                    status: 'DRAFT',
                    companySettingsSnapshot: settings || null,
                    ...calculatedState
                } as ChargeCalculation);

                if (state.payrollImportId) {
                    setSelectedImportId(state.payrollImportId);
                }
            } else if (id) {
                const data = await chargeCalculationService.getById(id);
                if (!data) {
                    toast.error('Cálculo não encontrado');
                    navigate('/expenses/charges');
                    return;
                }
                setCalculation(data);
                if (data.payrollImportId) {
                    setSelectedImportId(data.payrollImportId);
                }

                // Load payroll imports to populate dropdown
                const imports = await listPayrollImports();
                setAvailableImports(imports.filter(i => i.competence === data.competence));
            }
        } catch (error) {
            console.error(error);
            toast.error('Erro ao carregar dados');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [id]);

    const performCalculation = async () => {
        if (!calculation || !selectedImportId) {
            toast.error('Selecione uma importação de folha para calcular.');
            return;
        }

        setIsCalculating(true);
        try {
            // 1. Get Company Settings for the competence
            const date = `${calculation.competence}-01`;
            const settings = await companySettingsService.getSettingsForDate(date);

            if (!settings) {
                toast.error(`Nenhuma configuração de empresa encontrada vigente em ${date}. Configure em Admin > Configurações.`);
                setIsCalculating(false);
                return;
            }

            // 2. Get Payroll Data
            const { getPayrollImportById } = await import('@/features/Expenses/services');
            const payrollData = await getPayrollImportById(selectedImportId);

            if (!payrollData) {
                toast.error('Dados da folha não encontrados.');
                setIsCalculating(false);
                return;
            }

            // 3. Calculate per collaborator
            const appliedInssRate = (settings.inssEmployerRate || 0) / 100;
            const appliedCprbRate = (settings.cprbRate || 0) / 100;
            const appliedRatRate = ((settings.rat || 0) / 100) * (settings.fap || 1);
            const appliedThirdsRate = (settings.thirdParties || 0) / 100;

            const newItems: ChargeCalculationItem[] = payrollData.collaborators.map(col => {
                const base = col.earningsTotal || 0;

                let inss = 0;
                let rat = 0;
                let thirds = 0;

                if (settings.regime !== 'SIMPLES') {
                    inss = base * appliedInssRate;
                    rat = base * appliedRatRate;
                    thirds = base * appliedThirdsRate;
                }

                return {
                    collaboratorId: col.collaboratorId,
                    collaboratorName: col.collaboratorName,
                    baseValue: base,
                    inssCompany: inss,
                    rat: rat,
                    thirdParties: thirds,
                    total: inss + rat + thirds
                };
            });

            // 4. Update Calculation Record (IN MEMORY ONLY)
            const totalBase = newItems.reduce((sum, item) => sum + item.baseValue, 0);
            const totalInss = newItems.reduce((sum, item) => sum + item.inssCompany, 0);
            const totalRat = newItems.reduce((sum, item) => sum + item.rat, 0);
            const totalThirds = newItems.reduce((sum, item) => sum + item.thirdParties, 0);

            // Calculate CPRB on Revenue using adjusted rate
            const revenue = calculation.monthlyRevenue || 0;
            const totalCprb = revenue * appliedCprbRate;

            const totalCharges = totalInss + totalRat + totalThirds + totalCprb;

            // Snapshot settings
            const usedSettings = { ...settings };

            const updatedCalc: ChargeCalculation = {
                ...calculation,
                payrollImportId: selectedImportId,
                companySettingsSnapshot: usedSettings,
                items: newItems,
                totalBase,
                totalInssCompany: totalInss,
                totalRat: totalRat,
                totalThirdParties: totalThirds,
                totalCprb,
                totalCharges,
                status: 'CLOSED' // Or keep DRAFT until saved? Let's say CLOSED means "Valid Calculation"
            };

            setCalculation(updatedCalc);
            toast.success('Cálculo atualizado (não salvo). Clique em Salvar para confirmar.');

        } catch (error) {
            console.error(error);
            toast.error('Erro ao calcular.');
        } finally {
            setIsCalculating(false);
        }
    };

    const handleSave = async () => {
        if (!calculation) return;

        setLoading(true);
        try {
            if (id === 'new') {
                // Remove 'id' property or pass to create which ignores it or Omit it
                const { id: _, ...createPayload } = calculation;
                await chargeCalculationService.create(createPayload);
                toast.success('Cálculo salvo com sucesso!');
                navigate('/expenses/charges');
            } else if (id) {
                await chargeCalculationService.update(id, calculation);
                toast.success('Cálculo atualizado com sucesso!');
                navigate('/expenses/charges');
            }
        } catch (error) {
            console.error(error);
            toast.error('Erro ao salvar cálculo');
            setLoading(false);
        }
    };

    if (loading || !calculation) {
        return <div className="p-8 text-center text-gray-500">Carregando...</div>;
    }

    return (
        <div className="space-y-6">
            <SubPageHeader
                backTo="/expenses/charges"
                icon={<CalculatorIcon className="h-5 w-5" aria-hidden />}
                badge="Competência"
                title={calculation.competence}
                actions={
                    <div className="flex flex-col items-end text-xs text-gray-500">
                        {id !== 'new' && <span>ID: {calculation.id}</span>}
                    </div>
                }
            />

            {/* Configuration Card */}
            <div className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm col-span-1 sm:col-span-3">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                        <CalculatorIcon className="h-5 w-5 mr-2 text-gray-400" />
                        Parâmetros de Cálculo
                    </h3>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 items-end">
                        <div>
                            <CurrencyInput
                                id="monthlyRevenue"
                                name="monthlyRevenue"
                                label="Faturamento Mensal (para CPRB)"
                                value={calculation.monthlyRevenue || 0}
                                onValueChange={(val, name, values) => setCalculation({ ...calculation, monthlyRevenue: values.float })}
                                disabled={calculation.status === 'CLOSED'}
                            />
                        </div>

                        <div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={performCalculation}
                                    disabled={isCalculating || !selectedImportId}
                                    className="flex-1 h-[38px] inline-flex justify-center items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none"
                                >
                                    {isCalculating ? <ArrowPathIcon className="animate-spin h-5 w-5" /> : 'Calcular'}
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={isCalculating || loading}
                                    className="flex-1 h-[38px] inline-flex justify-center items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none"
                                >
                                    <CheckIcon className="h-5 w-5 mr-1" />
                                    Salvar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {calculation.companySettingsSnapshot && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
                    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="text-xs font-semibold uppercase text-gray-500">Regime Tributário</div>
                        <div className="mt-1 text-2xl font-semibold text-gray-900 truncate" title={calculation.companySettingsSnapshot.regime}>
                            {calculation.companySettingsSnapshot.regime}
                        </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="text-xs font-semibold uppercase text-gray-500">INSS Patronal</div>
                        <div className="mt-1 text-2xl font-semibold text-gray-900">
                            {calculation.companySettingsSnapshot.inssEmployerRate}
                        </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="text-xs font-semibold uppercase text-gray-500">RAT x FAP</div>
                        <div className="mt-1 text-2xl font-semibold text-gray-900">
                            {Number(calculation.companySettingsSnapshot.rat).toFixed(2)}% x {Number(calculation.companySettingsSnapshot.fap).toFixed(4)}
                        </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="text-xs font-semibold uppercase text-gray-500">Terceiros</div>
                        <div className="mt-1 text-2xl font-semibold text-gray-900">{calculation.companySettingsSnapshot.thirdParties}</div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="text-xs font-semibold uppercase text-gray-500">CPRB (Desoneração)</div>
                        <div className="mt-1">
                            {calculation.companySettingsSnapshot.cprbRate > 0 ? (
                                <div className="text-2xl font-semibold text-gray-900">
                                    {calculation.companySettingsSnapshot.cprbRate}%
                                </div>
                            ) : (
                                <div className="text-xl font-medium text-gray-400">N/A</div>
                            )}
                        </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-sm border-l-4 border-l-indigo-500">
                        <div className="text-xs font-semibold uppercase text-indigo-700">Folha de Referência</div>
                        <div className="mt-1 flex items-center h-8">
                            {calculation.payrollImportId ? (
                                <div className="text-xl font-semibold text-indigo-900 flex items-center truncate">
                                    {calculation.competence}
                                </div>
                            ) : (
                                <div className="text-sm font-medium text-amber-600">Nenhuma Vinculada</div>
                            )}
                        </div>
                    </div>
                </div>
            )}


            {/* Summary Cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
                <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="text-xs font-semibold uppercase text-gray-500">Base Total</div>
                    <div className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(calculation.totalBase)}</div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="text-xs font-semibold uppercase text-gray-500">INSS Patronal</div>
                    <div className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(calculation.totalInssCompany)}</div>
                    <p className="text-xs text-gray-500 mt-1">
                        {calculation.totalBase > 0 ? ((calculation.totalInssCompany / calculation.totalBase) * 100).toFixed(2) : '0.00'}%
                    </p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="text-xs font-semibold uppercase text-gray-500">RAT Ajustado</div>
                    <div className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(calculation.totalRat)}</div>
                    <p className="text-xs text-gray-500 mt-1">
                        {calculation.totalBase > 0 ? ((calculation.totalRat / calculation.totalBase) * 100).toFixed(2) : '0.00'}%
                    </p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="text-xs font-semibold uppercase text-gray-500">Terceiros</div>
                    <div className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(calculation.totalThirdParties)}</div>
                    <p className="text-xs text-gray-500 mt-1">
                        {calculation.totalBase > 0 ? ((calculation.totalThirdParties / calculation.totalBase) * 100).toFixed(2) : '0.00'}%
                    </p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="text-xs font-semibold uppercase text-gray-500">CPRB (Desoneração)</div>
                    <div className="mt-1">
                        {calculation.totalCprb > 0 ? (
                            <>
                                <div className="text-2xl font-semibold text-gray-900">{formatCurrency(calculation.totalCprb)}</div>
                                <p className="text-xs text-gray-500 mt-1">
                                    {calculation.totalBase > 0 ? ((calculation.totalCprb / calculation.totalBase) * 100).toFixed(2) : '0.00'}%
                                </p>
                            </>
                        ) : (
                            <div className="text-lg font-medium text-gray-400 mt-1">N/A</div>
                        )}
                    </div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-sm border-l-4 border-l-indigo-500">
                    <div className="text-xs font-semibold uppercase text-indigo-700">Total Encargos</div>
                    <div className="mt-1 text-2xl font-semibold text-indigo-900">{formatCurrency(calculation.totalCharges)}</div>
                    <p className="text-xs text-indigo-600 mt-1">
                        {calculation.totalBase > 0 ? ((calculation.totalCharges / calculation.totalBase) * 100).toFixed(2) : '0.00'}%
                    </p>
                </div>
            </div>

            {/* Collaborator List */}
            <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center">
                    <UsersIcon className="h-5 w-5 mr-2 text-gray-500" />
                    <h3 className="font-medium text-gray-700">Detalhamento por Colaborador</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Colaborador</th>
                                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Base Cálculo</th>
                                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">INSS Patr.</th>
                                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">RAT</th>
                                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Terceiros</th>
                                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Total</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {calculation.items.length === 0 ? (
                                <tr><td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">Nenhum colaborador processado. Realize o cálculo.</td></tr>
                            ) : (
                                calculation.items.map((item) => (
                                    <tr key={item.collaboratorId} className="hover:bg-gray-50">
                                        <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">{item.collaboratorName}</td>
                                        <td className="whitespace-nowrap px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(item.baseValue)}</td>
                                        <td className="whitespace-nowrap px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(item.inssCompany)}</td>
                                        <td className="whitespace-nowrap px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(item.rat)}</td>
                                        <td className="whitespace-nowrap px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(item.thirdParties)}</td>
                                        <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-semibold text-gray-900">{formatCurrency(item.total)}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

        </div >
    );
}
